if (!customElements.get('package-selector')) {
  class PackageSelector extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-package-list]');
      this.items = Array.from(this.querySelectorAll('[data-package-item]'));
      this.paginationButtons = Array.from(this.querySelectorAll('[data-package-pagination]'));
      this.images = Array.from(this.querySelectorAll('[data-package-image]'));
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.autoplayInterval = Math.max(Number(this.dataset.autoplayInterval) || 4000, 1000);
      this.isHovering = false;

      if (!this.list || !this.items.length) return;

      this.onPaginationClick = this.onPaginationClick.bind(this);
      this.onInputChange = this.onInputChange.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onUserScrollStart = this.onUserScrollStart.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onMouseEnter = this.onMouseEnter.bind(this);
      this.onMouseLeave = this.onMouseLeave.bind(this);

      this.paginationButtons.forEach((button) => {
        button.addEventListener('click', this.onPaginationClick);
      });
      this.addEventListener('change', this.onInputChange);
      this.list.addEventListener('scroll', this.onScroll, { passive: true });
      this.list.addEventListener('pointerdown', this.onUserScrollStart, { passive: true });
      this.list.addEventListener('wheel', this.onUserScrollStart, { passive: true });
      this.addEventListener('mouseenter', this.onMouseEnter);
      this.addEventListener('mouseleave', this.onMouseLeave);
      window.addEventListener('resize', this.onResize);

      const checkedInput = this.querySelector('[data-package-input]:checked');
      const initialIndex = Number(checkedInput?.dataset.packageInput ?? this.items.length - 1);

      this.setActivePackage(initialIndex);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.scrollToPackage(initialIndex, 'auto'));
      });

      this.startAutoplay();
    }

    disconnectedCallback() {
      this.paginationButtons?.forEach((button) => {
        button.removeEventListener('click', this.onPaginationClick);
      });
      this.removeEventListener('change', this.onInputChange);
      this.list?.removeEventListener('scroll', this.onScroll);
      this.list?.removeEventListener('pointerdown', this.onUserScrollStart);
      this.list?.removeEventListener('wheel', this.onUserScrollStart);
      this.removeEventListener('mouseenter', this.onMouseEnter);
      this.removeEventListener('mouseleave', this.onMouseLeave);
      window.removeEventListener('resize', this.onResize);
      this.stopAutoplay();
      clearTimeout(this.scrollTimeout);
      cancelAnimationFrame(this.scrollFrame);
      cancelAnimationFrame(this.resizeFrame);
    }

    onPaginationClick(event) {
      const index = Number(event.currentTarget.dataset.packagePagination);
      this.setActivePackage(index);
      this.scrollToPackage(index, 'smooth');
      this.restartAutoplay();
    }

    onInputChange(event) {
      if (!event.target.matches('[data-package-input]') || !event.target.checked) return;

      const index = Number(event.target.dataset.packageInput);
      this.setActivePackage(index);
      this.scrollToPackage(index, 'smooth');
      this.restartAutoplay();
    }

    onScroll() {
      if (this.isProgrammaticScroll) {
        clearTimeout(this.scrollTimeout);
        this.scrollTimeout = setTimeout(() => this.finishProgrammaticScroll(), 120);
        return;
      }

      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => {
        this.setActivePackage(this.getClosestPackageIndex());
      });
    }

    onUserScrollStart() {
      this.isProgrammaticScroll = false;
      this.pendingPackageIndex = null;
      clearTimeout(this.scrollTimeout);
      this.restartAutoplay();
    }

    onResize() {
      cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => {
        const activeItem = this.items.find((item) => item.classList.contains('is-active'));
        if (activeItem) this.scrollToPackage(Number(activeItem.dataset.packageItem), 'auto');
      });
    }

    onMouseEnter() {
      this.isHovering = true;
      this.stopAutoplay();
    }

    onMouseLeave() {
      this.isHovering = false;
      this.startAutoplay();
    }

    startAutoplay() {
      if (!this.autoplayEnabled || this.isHovering || this.items.length < 2 || this.autoplayTimer) return;

      this.autoplayTimer = setTimeout(() => {
        this.autoplayTimer = null;
        const activeIndex = this.items.findIndex((item) => item.classList.contains('is-active'));
        const nextIndex = (Math.max(activeIndex, 0) + 1) % this.items.length;

        this.setActivePackage(nextIndex);
        this.scrollToPackage(nextIndex, 'smooth');
        this.startAutoplay();
      }, this.autoplayInterval);
    }

    stopAutoplay() {
      clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }

    setActivePackage(index) {
      if (!Number.isInteger(index) || !this.items[index]) return;

      this.items.forEach((item, itemIndex) => {
        const isActive = itemIndex === index;
        const input = item.querySelector('[data-package-input]');

        item.classList.toggle('is-active', isActive);
        if (input) input.checked = isActive;
      });

      this.paginationButtons.forEach((button, buttonIndex) => {
        const isActive = buttonIndex === index;
        button.classList.toggle('is-active', isActive);

        if (isActive) {
          button.setAttribute('aria-current', 'true');
        } else {
          button.removeAttribute('aria-current');
        }
      });

      this.images.forEach((image) => {
        const isActive = Number(image.dataset.packageImage) === index;
        image.classList.toggle('is-active', isActive);
        image.setAttribute('aria-hidden', String(!isActive));
      });
    }

    scrollToPackage(index, behavior) {
      const item = this.items[index];
      if (!item || this.list.scrollWidth <= this.list.clientWidth + 1) return;

      const listRect = this.list.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      const itemLeft = itemRect.left - listRect.left + this.list.scrollLeft;
      const centeredLeft = itemLeft - (this.list.clientWidth - itemRect.width) / 2;
      const maxScrollLeft = this.list.scrollWidth - this.list.clientWidth;
      const targetLeft = Math.max(0, Math.min(centeredLeft, maxScrollLeft));

      this.isProgrammaticScroll = true;
      this.pendingPackageIndex = index;
      clearTimeout(this.scrollTimeout);
      this.list.scrollTo({ left: targetLeft, behavior });

      this.scrollTimeout = setTimeout(
        () => this.finishProgrammaticScroll(),
        behavior === 'smooth' ? 600 : 0
      );
    }

    finishProgrammaticScroll() {
      if (!this.isProgrammaticScroll) return;

      const index = this.pendingPackageIndex;
      this.isProgrammaticScroll = false;
      this.pendingPackageIndex = null;

      if (Number.isInteger(index)) this.setActivePackage(index);
    }

    getClosestPackageIndex() {
      const maxScrollLeft = this.list.scrollWidth - this.list.clientWidth;

      if (maxScrollLeft <= 1) {
        return Math.max(0, this.items.findIndex((item) => item.classList.contains('is-active')));
      }

      const scrollProgress = Math.max(0, Math.min(this.list.scrollLeft / maxScrollLeft, 1));
      return Math.round(scrollProgress * (this.items.length - 1));
    }
  }

  customElements.define('package-selector', PackageSelector);
}
